import { useCurrencies, useSearchExternalInstrumentsQuery } from 'mmfintech-backend-api'
import type { CurrencyType, PaymentInstrumentResponse } from 'mmfintech-backend-api/types'
import { isValidArray } from 'mmfintech-commons'
import { useEffect, useMemo, useState } from 'react'

type UseExpenseRecipientsProps = {
  currencyCode?: string
  foreignId?: string
  reimbursement?: boolean
  pageSize?: number
}

export const useExpenseRecipientsQry = (props: UseExpenseRecipientsProps) => {
  const { currencyCode, foreignId, reimbursement, pageSize = 1000 } = props

  const [filteredRecipients, setFilteredRecipients] = useState<PaymentInstrumentResponse[]>()

  const { getCurrencyType } = useCurrencies()

  const { recipients, recipientsError, recipientsFetching } = useSearchExternalInstrumentsQuery(
    { size: pageSize },
    {
      selectFromResult: ({ data, error, isFetching }) => ({
        recipients: isValidArray(data?.content) ? data.content : [],
        recipientsError: error,
        recipientsFetching: isFetching
      })
    }
  )

  const currencyType: CurrencyType = useMemo(() => getCurrencyType(currencyCode), [currencyCode])

  const extractForeignId = (recipient: PaymentInstrumentResponse): string =>
    'foreignId' in recipient ? String(recipient.foreignId) : ''

  useEffect(() => {
    if (isValidArray(recipients)) {
      const list = recipients?.filter(
        recipient =>
          !(
            (!recipient.type.includes('_BANK_ACCOUNT') && !recipient.type.includes('_CRYPTO_ADDRESS')) ||
            (currencyType === 'FIAT' && !recipient.type.includes('_BANK_ACCOUNT')) ||
            (currencyType === 'CRYPTO' &&
              recipient.type.includes('_CRYPTO_ADDRESS') &&
              currencyCode !== recipient.currency) ||
            (foreignId && String(foreignId) !== extractForeignId(recipient)) ||
            (reimbursement && !recipient.personal)
          )
      )
      setFilteredRecipients(list)
    } else {
      setFilteredRecipients(null)
    }
  }, [recipients, foreignId, reimbursement, currencyCode, currencyType])

  return {
    recipients: filteredRecipients,
    recipientsError,
    recipientsFetching
  }
}
